'use client';

import { Search, X } from 'lucide-react';
import { FormEvent, useState } from 'react';

interface SearchInputWithButtonProps {
  onSearch: (value: string) => void;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

export function SearchInputWithButton({
  onSearch,
  placeholder = 'Search...',
  className = '',
  'aria-label': ariaLabel,
  value,
  onValueChange,
  secondaryActionLabel,
  onSecondaryAction,
}: SearchInputWithButtonProps) {
  const [internalValue, setInternalValue] = useState('');
  const inputValue = value ?? internalValue;

  const setValue = (nextValue: string) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }

    onValueChange?.(nextValue);
  };

  const handleSearch = () => {
    onSearch(inputValue);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSearch();
  };

  return (
    <form onSubmit={handleSubmit} className={`flex w-full items-center gap-2 ${className}`}>
      <div className="relative flex-1">
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setValue(e.target.value)}
          className="h-12 w-full rounded-xl border border-transparent bg-white pl-4 pr-10 text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={ariaLabel || placeholder}
          autoComplete="off"
        />
        {inputValue.length > 0 && (
          <button
            type="button"
            onClick={() => setValue('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full border border-border text-foreground-muted transition hover:border-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X size={12} aria-hidden="true" />
          </button>
        )}
      </div>
      {secondaryActionLabel && onSecondaryAction && (
        <button
          type="button"
          onClick={onSecondaryAction}
          className="h-12 shrink-0 rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground transition hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {secondaryActionLabel}
        </button>
      )}
      <button
        type="submit"
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-action-green text-black transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Search"
      >
        <Search size={20} aria-hidden="true" />
        <span className="sr-only">Search</span>
      </button>
    </form>
  );
}

